/**
 * One recommendation candidate.
 *
 * The round detail page shows the first choice large and the alternates small,
 * and each card can expand into the same structured numbers panel. The variant
 * only changes emphasis, never the information: the numbers behind an alternate
 * are as inspectable as the first choice's.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { RecommendationDto } from '@shared/types'

import { cn, formatProbability, formatScore } from '../../lib/format'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { RecommendationDetailsPanel } from './recommendation-details'

export interface RecommendationCardProps {
  recommendation: RecommendationDto
  /** `primary` is the first choice, `alternate` one of the smaller cards. */
  variant: 'primary' | 'alternate'
  /** One-based position of an alternate, used for its label. */
  alternateIndex?: number
  /** True when the group has locked this candidate in. */
  decided?: boolean
  /**
   * Organizer-only finalize action. Absent for everyone else, and for rounds
   * that are already locked or belong to an archived event.
   */
  onDecide?: () => void
  /** True while the finalize request is in flight. */
  deciding?: boolean
  className?: string
}

export function RecommendationCard({
  recommendation,
  variant,
  alternateIndex,
  decided = false,
  onDecide,
  deciding = false,
  className,
}: RecommendationCardProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  const isPrimary = variant === 'primary'
  const confidence = formatProbability(recommendation.details.scoring.confidence)
  const tags = recommendation.details.district.tags
  // The model's first choice is promoted to the top by design, so a first card
  // that the aggregation policy did not rank first has to say why it is here.
  const promoted = isPrimary && recommendation.engineRank > 1

  return (
    <Card className={cn(isPrimary ? 'border-primary/40' : undefined, className)}>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={isPrimary ? 'default' : 'secondary'}>
            {isPrimary
              ? t('recommend.primary')
              : t('recommend.alternate', { index: alternateIndex ?? 1 })}
          </Badge>
          {decided ? <Badge variant="success">{t('recommend.decided')}</Badge> : null}
        </div>
        <CardTitle className={isPrimary ? 'text-lg sm:text-xl' : 'text-base'}>
          {recommendation.districtName}
        </CardTitle>
        {promoted ? (
          <CardDescription>{t('recommend.modelPrimary', { rank: recommendation.engineRank })}</CardDescription>
        ) : null}
        {recommendation.details.district.parentName ? (
          <CardDescription>
            {t('recommend.insideRegion', {
              region: recommendation.details.district.parentName,
            })}
          </CardDescription>
        ) : null}
        {recommendation.details.district.address ? (
          <CardDescription>{recommendation.details.district.address}</CardDescription>
        ) : null}
        <div className="flex flex-wrap gap-1.5 pt-1">
          <Badge variant="outline" title={t('recommend.scoreHint')}>
            {t('recommend.score', { score: formatScore(recommendation.score) })}
          </Badge>
          {confidence ? (
            <Badge variant="outline" title={t('recommend.confidenceHint')}>
              {t('recommend.confidence', { confidence })}
            </Badge>
          ) : null}
          {recommendation.details.scoring.source === 'fallback' ? (
            <Badge variant="warning">{t('recommend.scoringSourceFallback')}</Badge>
          ) : null}
          {/* The round-level notice says the first choice needs a check; this is the
              per-card state, so a reader can see which candidate it refers to
              instead of guessing whether it applies to the round or one card. */}
          {recommendation.details.scoring.source === 'jev' &&
          recommendation.details.scoring.needsReview ? (
            <Badge variant="warning" title={t('recommend.confidenceHint')}>
              {t('recommend.lowConfidenceBadge')}
            </Badge>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <Badge key={tag} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
          >
            {t('recommend.details')}
          </Button>
          {onDecide ? (
            <Button size="sm" disabled={deciding} onClick={onDecide}>
              {deciding ? t('recommend.deciding') : t('recommend.decide')}
            </Button>
          ) : null}
        </div>

        {expanded ? <RecommendationDetailsPanel details={recommendation.details} /> : null}
      </CardContent>
    </Card>
  )
}
